
import React from 'react';
import { Link } from 'react-router-dom';

const Implementation: React.FC = () => {
  return (
    <>
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue">Implementing Zero-Trust: A Cost-Effective Approach</h2>
      
      <p className="mb-6 text-gray-700 leading-relaxed">
        The encouraging news is that adopting zero-trust principles doesn't necessarily demand a complete overhaul of your IT infrastructure 
        or massive financial investment. Below is a practical, step-by-step approach designed specifically for resource-conscious small businesses:
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">1. Start with Identity and Access Management (IAM)</h3>
      
      <p className="mb-4 text-gray-700 leading-relaxed">
        The cornerstone of zero-trust is precisely knowing who's accessing your systems and data at all times.
      </p>
      
      <ul className="list-disc ml-6 mb-4 text-gray-700 leading-relaxed">
        <li className="mb-2">Deploy multi-factor authentication (MFA) across all business applications</li>
        <li className="mb-2">Utilize single sign-on (SSO) solutions for centralized access management</li>
        <li className="mb-2">Regularly audit user accounts and eliminate unnecessary access privileges</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed italic">
        <strong>Budget-friendly options:</strong> Microsoft Authenticator, Google Authenticator, and Duo Security provide free or low-cost MFA solutions for small businesses.
        See our <Link to="/blog/cloud-migration-guide-small-businesses" className="text-itmgmt-medium-blue">cloud migration guide</Link> for information on integrating these tools with cloud platforms.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">2. Segment Your Network</h3>
      
      <p className="mb-4 text-gray-700 leading-relaxed">
        Network segmentation establishes internal boundaries between different sections of your business network, 
        restricting how far an attacker can move if they compromise one segment.
      </p>
      
      <ul className="list-disc ml-6 mb-4 text-gray-700 leading-relaxed">
        <li className="mb-2">Isolate critical systems and data from general office networks</li>
        <li className="mb-2">Implement VLANs (Virtual Local Area Networks) to create logical divisions</li>
        <li className="mb-2">Deploy internal firewalls between segments</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed italic">
        <strong>Practical approach:</strong> Most contemporary business routers support basic network segmentation capabilities. 
        Begin by separating point-of-sale systems, financial data, and customer information from general employee access networks.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">3. Apply the Least Privilege Principle</h3>
      
      <p className="mb-4 text-gray-700 leading-relaxed">
        Every user should receive access exclusively to the specific resources needed for their job responsibilities – nothing more.
      </p>
      
      <ul className="list-disc ml-6 mb-4 text-gray-700 leading-relaxed">
        <li className="mb-2">Review and revise access permissions for all employees</li>
        <li className="mb-2">Establish role-based access controls rather than individual permissions</li>
        <li className="mb-2">Implement time-limited access for contractors or temporary requirements</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed italic">
        <strong>Implementation tip:</strong> Begin with your most sensitive data and systems, then work outward. 
        Document the minimum access requirements for each role in your organization. Our <Link to="/services/cybersecurity" className="text-itmgmt-medium-blue">cybersecurity service</Link> includes 
        assistance with creating these access policies.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">4. Enable Continuous Monitoring and Verification</h3>
      
      <p className="mb-4 text-gray-700 leading-relaxed">
        Zero-trust demands ongoing vigilance, not merely one-time security measures.
      </p>
      
      <ul className="list-disc ml-6 mb-4 text-gray-700 leading-relaxed">
        <li className="mb-2">Implement endpoint detection and response (EDR) solutions</li>
        <li className="mb-2">Enable comprehensive logging across all systems and regularly review logs</li>
        <li className="mb-2">Configure alerts for unusual access patterns or behaviors</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed italic">
        <strong>Affordable options:</strong> Solutions like Wazuh (open-source security monitoring) and Microsoft Defender for Business 
        deliver robust monitoring capabilities at small business-friendly price points. Learn more about preventing costly downtime in our 
        guide on <Link to="/blog/cost-of-it-downtime-prevention" className="text-itmgmt-medium-blue">the true cost of IT downtime</Link>.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">5. Secure Your Cloud Resources</h3>
      
      <p className="mb-4 text-gray-700 leading-relaxed">
        As small businesses increasingly depend on cloud services, extending zero-trust principles to these environments is crucial.
      </p>
      
      <ul className="list-disc ml-6 mb-4 text-gray-700 leading-relaxed">
        <li className="mb-2">Enable MFA for all cloud service accounts</li>
        <li className="mb-2">Utilize cloud security posture management tools to identify misconfigurations</li>
        <li className="mb-2">Implement data loss prevention policies</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed italic">
        <strong>Quick win:</strong> Most major cloud providers offer security assessment tools as part of their basic service packages. 
        Run these assessments monthly at minimum. Our <Link to="/services/cloud-solutions" className="text-itmgmt-medium-blue">cloud solutions</Link> team can help you implement robust cloud security.
      </p>
    </>
  );
};

export default Implementation;
